import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApplicationApiClient } from '@nk/api-client-lib';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureDims,
  AppFeatureEntity,
  AppFeatureKey,
  AppFeaturePackageCustomerAccessComposite,
  AppFeaturePackageCustomerAccessEntity,
  AppFeaturePackageEntity,
  AppFeaturePackageMembershipAssignmentComposite,
  AppFeaturePackageMembershipAssignmentEntity,
  AppSubscriptionComposite,
  COUNTRY_FEATURES,
  WithUuid,
} from '@nk/data-entities-lib';
import { Observable, of } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { AppSubscriptionStateDetails, appSubscriptionStateDetails } from '../utils/subscription.utils';
import { AppService } from './app.service';

export const FEATURE_KEY_TO_COLLATERAL_TYPE_KEYS = {
  [AppFeatureKey.norwegian_property_data]: [
    'norwegian_mapping_authority^^^lien',
    'norwegian_mapping_authority^^^registered_forced_lien',
    'norwegian_mapping_authority^^^registered_lien',
    'norwegian_mapping_authority^^^enforcement_sale',
  ],
};

export const ROLE_KEY_TO_COLLATERAL_TYPE_KEYS = {
  [AppFeatureKey.norwegian_property_data]: [
    'norwegian_mapping_authority^^^lien',
    'norwegian_mapping_authority^^^registered_forced_lien',
    'norwegian_mapping_authority^^^registered_lien',
    'norwegian_mapping_authority^^^enforcement_sale',
  ],
};

@Injectable({
  providedIn: 'root',
})
export class AppFeatureService {
  constructor(
    private _applicationApi: ApplicationApiClient,
    private _appService: AppService,
    private _cachingService: CachingService,
  ) {}

  public fetchAppFeaturePackageCustomerAccess(
    onlySubscriptionsFlag?: boolean,
  ): Observable<AppFeaturePackageCustomerAccessEntity[]> {
    let httpParams = new HttpParams();
    if (!(onlySubscriptionsFlag == null)) {
      httpParams = httpParams.append('only_subscriptions_flag', onlySubscriptionsFlag.toString());
    }

    return this._appService.identityDataCache.get().pipe(
      mergeMap((identity) => {
        return this._applicationApi.get<AppFeaturePackageCustomerAccessEntity[]>(
          `app-customer/${identity.app_customer.uuid}/app-feature-package-customer-access`,
          { params: httpParams },
          null,
        );
      }),
    );
  }

  public fetchAppFeaturePackageCustomerAccessByCustomer(
    customerUuid: string,
  ): Observable<AppFeaturePackageCustomerAccessEntity[]> {
    return this._applicationApi.get<AppFeaturePackageCustomerAccessEntity[]>(
      `app-customer/${customerUuid}/app-feature-package-customer-access`,
      {},
      [],
    );
  }

  public fetchAppFeaturePackageMembershipCompositeListByCustomerUuid(
    appCustomerUuid: string,
  ): Observable<AppFeaturePackageMembershipAssignmentComposite[]> {
    return this._applicationApi.get<AppFeaturePackageMembershipAssignmentComposite[]>(
      `app-customer/${appCustomerUuid}/app-feature-package-membership-assignment-composite`,
      {},
      [],
    );
  }

  public fetchAppSubscriptionComposite(
    appCustomerUuid: string,
    appSubscriptionUuid: string,
  ): Observable<AppSubscriptionComposite> {
    return this._applicationApi.get<AppSubscriptionComposite>(
      `app-customer/${appCustomerUuid}/app-subscription-composite/${appSubscriptionUuid}`,
      {},
      [],
      [],
    );
  }

  public postAppFeaturePackageMembershipAssignment(
    appCustomerUuid: string,
    payload: Partial<AppFeaturePackageMembershipAssignmentEntity>,
  ): Observable<AppFeaturePackageMembershipAssignmentEntity> {
    // TODO Self-service: Payload is missing typing.
    return this._applicationApi.post<AppFeaturePackageMembershipAssignmentEntity>(
      `app-customer/${appCustomerUuid}/app-feature-package-membership-assignment`,
      payload,
      {},
      null,
    );
  }

  public deleteFeaturePackageMembershipAssignment(
    appCustomerUuid: string,
    appFeaturePackageMembershipAssignmentUuid: string,
  ): Observable<AppFeaturePackageMembershipAssignmentEntity> {
    return this._applicationApi.delete(
      `app-customer/${appCustomerUuid}/app-feature-package-membership-assignment/${appFeaturePackageMembershipAssignmentUuid}`,
      {},
      null,
    );
  }

  public appFeaturePackageCache = this._cachingService.create(this, this.fetchAppFeaturePackage);
  public fetchAppFeaturePackage(): Observable<AppFeaturePackageEntity[]> {
    return this._applicationApi.get<AppFeaturePackageEntity[]>('app-feature-package', {}, null);
  }

  public fetchAppFeaturePackageByUuid(
    customerUuid: string,
    featurePackageUuid: string,
  ): Observable<AppFeaturePackageEntity> {
    return this._applicationApi.get<AppFeaturePackageEntity>(
      `app-customer/${customerUuid}/app-feature-package/${featurePackageUuid}`,
      {},
      null,
    );
  }

  public appFeatureListCache = this._cachingService.create(this, this.fetchAppFeatureList);
  public fetchAppFeatureList(): Observable<AppFeatureEntity[]> {
    return this._applicationApi.get<AppFeatureEntity[]>('app-feature', {}, null);
  }

  public appFeaturePackageCustomerAccessCompositeCache = this._cachingService.create(
    this,
    this.fetchAppFeaturePackageCustomerAccessComposite,
  );
  public fetchAppFeaturePackageCustomerAccessComposite(): Observable<AppFeaturePackageCustomerAccessComposite[]> {
    return this._appService.identityDataCache.get().pipe(
      mergeMap((identity) => {
        return this._applicationApi.get<AppFeaturePackageCustomerAccessComposite[]>(
          `app-customer/${identity.app_customer.uuid}/app-feature-package-customer-access-composite`,
          {},
          [],
          [],
        );
      }),
    );
  }

  public appSubscriptionState(): Observable<AppSubscriptionStateDetails> {
    return this.appFeaturePackageCustomerAccessCompositeCache.get().pipe(
      map((composite) => {
        return appSubscriptionStateDetails(composite);
      }),
    );
  }

  public claimValidAppFeatureKeys(
    featureKeys: AppFeatureKey[] = [],
    appMembershipUuid?: string,
  ): Observable<AppFeatureKey[]> {
    if (!featureKeys.length) {
      return of<AppFeatureKey[]>([]);
    }
    const checkedFeatureKeys = featureKeys.filter((key) => AppFeatureKey[key]);
    return this.activeAppFeatureDimsCache
      .get(appMembershipUuid)
      .pipe(map((featureDims) => checkedFeatureKeys.filter((key) => featureDims.flags[key])));
  }

  public claimDefaultCountryFeatures(): Observable<AppFeatureKey[]> {
    return this.claimValidAppFeatureKeys([AppFeatureKey.base, ...COUNTRY_FEATURES]);
  }

  public activeAppFeatureDimsCache = this._cachingService.create(this, this.fetchActiveAppFeatureDims);
  public fetchActiveAppFeatureDims(appMembershipUuid?: string): Observable<AppFeatureDims> {
    return this.appFeatureListByMembershipCache
      .get(appMembershipUuid)
      .pipe(map((features) => new AppFeatureDims(features)));
  }

  public appFeatureListByMembershipCache = this._cachingService.create(this, this.fetchAppFeatureListByMembership);
  public fetchAppFeatureListByMembership(appMembershipUuid?: string): Observable<WithUuid<AppFeatureEntity>[]> {
    if (appMembershipUuid) {
      return this._applicationApi.get<WithUuid<AppFeatureEntity>[]>(`app-membership/${appMembershipUuid}/app-feature`);
    } else {
      return this._appService.identityDataCache.get().pipe(
        switchMap((identity) => {
          // NOTE: This is an open endpoint, used to check what features are available for the user, so we send an empty list of featurekeys

          return this._applicationApi.get<WithUuid<AppFeatureEntity>[]>(
            `app-membership/${identity.app_membership.uuid}/app-feature`,
            {},
            [],
            [],
          );
        }),
      );
    }
  }
}
